import React, { useState, useEffect, useRef, useCallback } from 'react';
import axiosInstance from '../config/axios';
import { X, Send, ImagePlus, Trash2 } from 'lucide-react';
import './ChatModal.css';

const POLL_MESSAGES_MS = 12000;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

function ChatModal({ conversationId, otherPartyDisplayName, onClose, currentParticipant, onDeleteConversation }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [attachmentBlobUrls, setAttachmentBlobUrls] = useState({});
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const fetchMessages = useCallback(async () => {
    if (!conversationId) return;
    try {
      const res = await axiosInstance.get(`/api/tenant/chat/conversations/${conversationId}/messages`);
      setMessages(res.data.messages || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load messages');
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  useEffect(() => {
    if (!conversationId) return;
    const t = setInterval(fetchMessages, POLL_MESSAGES_MS);
    return () => clearInterval(t);
  }, [conversationId, fetchMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    const withAttachment = messages.filter(m => m.attachment_path || m.attachment_filename);
    if (withAttachment.length === 0) return;
    let cancelled = false;
    const next = {};
    (async () => {
      for (const m of withAttachment) {
        if (cancelled || attachmentBlobUrls[m.id]) continue;
        try {
          const res = await axiosInstance.get(`/api/tenant/chat/attachments/${m.id}`, { responseType: 'blob' });
          if (!cancelled) next[m.id] = URL.createObjectURL(res.data);
        } catch (_) {}
      }
      if (!cancelled && Object.keys(next).length > 0) {
        setAttachmentBlobUrls(prev => ({ ...prev, ...next }));
      }
    })();
    return () => { cancelled = true; };
  }, [messages.length]);

  const sendMessage = async (text, file) => {
    if (!conversationId) return;
    setSending(true);
    setError('');
    try {
      if (file) {
        const form = new FormData();
        form.append('body', text || '');
        form.append('attachment', file);
        await axiosInstance.post(`/api/tenant/chat/conversations/${conversationId}/messages`, form, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await axiosInstance.post(`/api/tenant/chat/conversations/${conversationId}/messages`, { body: text || '' });
      }
      setBody('');
      await fetchMessages();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = (body || '').trim();
    const file = fileInputRef.current?.files?.[0];
    if (!trimmed && !file) return;
    sendMessage(trimmed, file || null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAttach = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_SIZE) {
      setError('Image must be 5MB or less');
      e.target.value = '';
      return;
    }
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setError('Only JPEG, PNG, GIF, or WebP allowed');
      e.target.value = '';
      return;
    }
    sendMessage(body.trim() || '(image)', file);
    e.target.value = '';
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file && file.type.startsWith('image/')) {
          e.preventDefault();
          if (file.size > MAX_IMAGE_SIZE) {
            setError('Image must be 5MB or less');
            return;
          }
          sendMessage(body.trim() || '(image)', file);
          return;
        }
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleDeleteConversation = async () => {
    if (!conversationId || !window.confirm('Delete this conversation? This will remove it for both you and the other party.')) return;
    setSending(true);
    setError('');
    try {
      await axiosInstance.delete(`/api/tenant/chat/conversations/${conversationId}`);
      onClose();
      if (onDeleteConversation) onDeleteConversation();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete conversation');
    } finally {
      setSending(false);
    }
  };

  const isMe = (msg) => {
    const me = currentParticipant || window.__chatMe;
    if (!me) return false;
    return msg.sender_type === me.type && String(msg.sender_id) === String(me.id);
  };

  return (
    <div className="chat-modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label="Chat">
      <div className="chat-modal" onClick={e => e.stopPropagation()}>
        <div className="chat-modal-header">
          <h3 className="chat-modal-title">Chat with {otherPartyDisplayName || 'Unknown'}</h3>
          <div className="chat-modal-header-actions">
            {onDeleteConversation && (
              <button type="button" className="chat-modal-btn-delete" onClick={handleDeleteConversation} disabled={sending} title="Delete conversation" aria-label="Delete conversation">
                <Trash2 size={18} />
              </button>
            )}
            <button type="button" className="chat-modal-close" onClick={onClose} aria-label="Close">
              <X size={20} />
            </button>
          </div>
        </div>
        {error && <div className="chat-modal-error">{error}</div>}
        <div className="chat-modal-messages">
          {loading ? (
            <div className="chat-modal-loading">Loading...</div>
          ) : messages.length === 0 ? (
            <div className="chat-modal-empty">No messages yet. Send one below.</div>
          ) : (
            messages.map(msg => {
              const me = isMe(msg);
              const blobUrl = attachmentBlobUrls[msg.id];
              return (
                <div key={msg.id} className={'chat-message ' + (me ? 'chat-message-me' : 'chat-message-them')}>
                  <div className="chat-message-bubble">
                    {msg.body && <div className="chat-message-body">{msg.body}</div>}
                    {(msg.attachment_path || msg.attachment_filename) && (
                      <div className="chat-message-attachment">
                        {blobUrl ? (
                          <img src={blobUrl} alt={msg.attachment_filename || 'Attachment'} className="chat-message-img" />
                        ) : (
                          <span className="chat-message-img-placeholder">Loading...</span>
                        )}
                      </div>
                    )}
                    <div className="chat-message-meta">{new Date(msg.created_at).toLocaleString()}</div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
        <form className="chat-modal-form" onSubmit={handleSubmit}>
          <input
            type="file"
            ref={fileInputRef}
            accept={ALLOWED_IMAGE_TYPES.join(',')}
            onChange={handleFileChange}
            className="chat-modal-file-input"
            aria-hidden="true"
            tabIndex={-1}
          />
          <textarea
            className="chat-modal-input"
            value={body}
            onChange={e => setBody(e.target.value)}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
            placeholder="Type a message... (Shift+Enter for new line)"
            rows={2}
            disabled={sending}
          />
          <div className="chat-modal-actions">
            <button type="button" className="chat-modal-btn-attach" onClick={handleAttach} title="Attach image" disabled={sending}>
              <ImagePlus size={20} />
            </button>
            <button type="submit" className="chat-modal-btn-send" disabled={sending}>
              <Send size={18} /> Send
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ChatModal;